import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col } from '@/components/ui';
import { Asset, AssetStatus } from '../../../types/Asset';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { AssetFilterOptions } from '../../../services/assetService';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../../../constants/assetTypes';
import { SearchableDropdown } from '../../../components/SearchableDropdown';

interface AssetModalProps {
  show: boolean;
  onHide: () => void;
  asset: Asset | null;
  onSave: (assetData: any) => void;
  filterOptions: AssetFilterOptions;
}

const AssetModal: React.FC<AssetModalProps> = ({
  show,
  onHide,
  asset,
  onSave,
  filterOptions,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assetType: '',
    assetNumber: '',
    subtype: '',
    contractAreaId: '',
    startLatitude: '',
    startLongitude: '',
    endLatitude: '',
    endLongitude: '',
    highwayUnique: '',
    parentAsset: '',
    numberedRoad: false,
    summerClass: '',
    winterClass: '',
    length: '',
    status: AssetStatus.Active,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (asset) {
      const assetType = asset.assetType || '';
      const isStructure = ['structure', 'culvert', 'sign', 'cattleguard'].includes(assetType);
      const subtype = asset.subtype || '';
      const name = asset.name || '';
      const assetNumber = asset.assetNumber || '';
      
      // Auto-generate description for structure assets
      let description = asset.description || '';
      if (isStructure && subtype && name && assetNumber) {
        description = `${subtype} - ${name} (#${assetNumber})`;
      } else if (isStructure && subtype && name) {
        description = `${subtype} - ${name}`;
      } else if (isStructure && subtype) {
        description = subtype;
      }
      
      setFormData({
        name,
        description,
        assetType,
        assetNumber,
        subtype,
        contractAreaId: asset.contractAreaId?.toString() || '',
        startLatitude: asset.startLatitude?.toString() || '',
        startLongitude: asset.startLongitude?.toString() || '',
        endLatitude: asset.endLatitude?.toString() || '',
        endLongitude: asset.endLongitude?.toString() || '',
        highwayUnique: asset.highwayUnique || '',
        parentAsset: asset.parentAsset || '',
        numberedRoad: asset.numberedRoad || false,
        summerClass: asset.summerClass || '',
        winterClass: asset.winterClass || '',
        length: asset.length?.toString() || '',
        status: asset.status || AssetStatus.Active,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        assetType: '',
        assetNumber: '',
        subtype: '',
        contractAreaId: '',
        startLatitude: '',
        startLongitude: '',
        endLatitude: '',
        endLongitude: '',
        highwayUnique: '',
        parentAsset: '',
        numberedRoad: false,
        summerClass: '',
        winterClass: '',
        length: '',
        status: AssetStatus.Active,
      });
    }
    setErrors({});
  }, [asset, show]);

  // Auto-generate description when asset type changes to structure
  useEffect(() => {
    const isStructure = ['structure', 'culvert', 'sign', 'cattleguard'].includes(formData.assetType);
    if (isStructure) {
      if (formData.subtype && formData.name && formData.assetNumber) {
        setFormData(prev => ({
          ...prev,
          description: `${prev.subtype} - ${prev.name} (#${prev.assetNumber})`
        }));
      } else if (formData.subtype && formData.name) {
        setFormData(prev => ({
          ...prev,
          description: `${prev.subtype} - ${prev.name}`
        }));
      } else if (formData.subtype) {
        setFormData(prev => ({
          ...prev,
          description: prev.subtype
        }));
      }
    }
  }, [formData.assetType, formData.subtype, formData.name, formData.assetNumber]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    const isStructure = ['structure', 'culvert', 'sign', 'cattleguard'].includes(formData.assetType);
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: finalValue
      };
      
      // Auto-generate description for structure assets when subtype, name, or assetNumber changes
      if (isStructure && (name === 'subtype' || name === 'name' || name === 'assetNumber')) {
        const subtype = name === 'subtype' ? value : updated.subtype;
        const assetName = name === 'name' ? value : updated.name;
        const assetNumber = name === 'assetNumber' ? value : updated.assetNumber;
        
        if (subtype && assetName && assetNumber) {
          updated.description = `${subtype} - ${assetName} (#${assetNumber})`;
        } else if (subtype && assetName) {
          updated.description = `${subtype} - ${assetName}`;
        } else if (subtype) {
          updated.description = subtype;
        }
      }
      
      return updated;
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleContractAreaChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      contractAreaId: value
    }));

    if (errors.contractAreaId) {
      setErrors(prev => ({
        ...prev,
        contractAreaId: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.assetNumber.trim()) {
      newErrors.assetNumber = 'Asset number is required';
    }

    if (!formData.assetType) {
      newErrors.assetType = 'Asset type is required';
    }

    if (!formData.contractAreaId) {
      newErrors.contractAreaId = 'Contract area is required';
    }

    // Subtype is mandatory for structure assets
    const isStructure = ['structure', 'culvert', 'sign', 'cattleguard'].includes(formData.assetType);
    if (isStructure && !formData.subtype?.trim()) {
      newErrors.subtype = 'Subtype is required for structure assets';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      contractAreaId: formData.contractAreaId ? parseInt(formData.contractAreaId) : null,
      startLatitude: formData.startLatitude ? parseFloat(formData.startLatitude) : null,
      startLongitude: formData.startLongitude ? parseFloat(formData.startLongitude) : null,
      endLatitude: formData.endLatitude ? parseFloat(formData.endLatitude) : null,
      endLongitude: formData.endLongitude ? parseFloat(formData.endLongitude) : null,
    };

    onSave(submitData);
  };

  const modalFooter = (
    <div className="tw-flex tw-justify-end tw-w-full tw-gap-2">
      <Button variant="secondary" onClick={onHide}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit}>
        {asset ? 'Update' : 'Create'} Asset
      </Button>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen={show}
      onClose={onHide}
      title={asset ? 'Edit Asset' : 'Create New Asset'}
      footer={modalFooter}
    >
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                isInvalid={!!errors.name}
                placeholder="Enter asset name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Asset Number *</Form.Label>
              <Form.Control
                type="text"
                name="assetNumber"
                value={formData.assetNumber}
                onChange={handleInputChange}
                isInvalid={!!errors.assetNumber}
                placeholder="Enter asset number"
              />
              <Form.Control.Feedback type="invalid">
                {errors.assetNumber}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Asset Type *</Form.Label>
              <Form.Select
                name="assetType"
                value={formData.assetType}
                onChange={handleInputChange}
                isInvalid={!!errors.assetType}
              >
                <option value="">Select asset type</option>
                {Object.values(ASSET_TYPES).map(type => (
                  <option key={type} value={type}>
                    {ASSET_TYPE_LABELS[type]}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.assetType}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Contract Area *</Form.Label>
              <SearchableDropdown
                options={filterOptions.contractAreas}
                value={formData.contractAreaId}
                onChange={handleContractAreaChange}
                getValue={(area) => area.id.toString()}
                formatOption={(area) => area.name}
                placeholder="Select contract area"
                className={errors.contractAreaId ? 'is-invalid' : ''}
                label=""
              />
              {errors.contractAreaId && (
                <div className="invalid-feedback tw-block">
                  {errors.contractAreaId}
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="tw-mb-3">
          <Form.Label>Description</Form.Label>
          {(formData.assetType === 'structure' || formData.assetType === 'culvert' || formData.assetType === 'sign' || formData.assetType === 'cattleguard') ? (
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              readOnly
              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
            />
          ) : (
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter asset description"
            />
          )}
        </Form.Group>

        {/* Show Subtype field for structures - Mandatory */}
        {(formData.assetType === 'structure' || formData.assetType === 'culvert' || formData.assetType === 'sign' || formData.assetType === 'cattleguard') && (
          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Subtype *</Form.Label>
                <Form.Control
                  type="text"
                  name="subtype"
                  value={formData.subtype}
                  onChange={handleInputChange}
                  isInvalid={!!errors.subtype}
                  placeholder="Enter subtype"
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.subtype}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        )}

        {formData.assetType === 'road' && (
          <Row>
            <Col md={4}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Road Length (km)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="length"
                  value={formData.length}
                  onChange={handleInputChange}
                  placeholder="Enter length in kilometers"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Winter Class</Form.Label>
                <Form.Control
                  type="text"
                  name="winterClass"
                  value={formData.winterClass}
                  onChange={handleInputChange}
                  placeholder="Enter winter class"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Summer Class</Form.Label>
                <Form.Control
                  type="text"
                  name="summerClass"
                  value={formData.summerClass}
                  onChange={handleInputChange}
                  placeholder="Enter summer class"
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        <Row>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Highway Unique</Form.Label>
              <Form.Control
                type="text"
                name="highwayUnique"
                value={formData.highwayUnique}
                onChange={handleInputChange}
                placeholder="Enter highway unique identifier"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Parent Asset</Form.Label>
              <Form.Control
                type="text"
                name="parentAsset"
                value={formData.parentAsset}
                onChange={handleInputChange}
                placeholder="Enter parent asset"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Start Latitude</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="startLatitude"
                value={formData.startLatitude}
                onChange={handleInputChange}
                placeholder="Enter start latitude"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Start Longitude</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="startLongitude"
                value={formData.startLongitude}
                onChange={handleInputChange}
                placeholder="Enter start longitude"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>End Latitude</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="endLatitude"
                value={formData.endLatitude}
                onChange={handleInputChange}
                placeholder="Enter end latitude"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>End Longitude</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="endLongitude"
                value={formData.endLongitude}
                onChange={handleInputChange}
                placeholder="Enter end longitude"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Check
                type="checkbox"
                name="numberedRoad"
                checked={formData.numberedRoad}
                onChange={handleInputChange}
                label="Numbered Road"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="tw-mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value={AssetStatus.Active}>Active</option>
                <option value={AssetStatus.Disabled}>Disabled</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </ResponsiveModal>
  );
};

export default AssetModal;